import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, RefreshCw, Trash2, Plus, Check, AlertTriangle } from 'lucide-react';
import { Agent, WhitelistUser, LINEIntegration } from '@/types';
import { getAgentSettings, updateAgentSettings, updateAgentStatus, addWhitelistUser, removeWhitelistUser } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const settingsSchema = z.object({
  displayName: z.string().min(1, '顯示名稱為必填').max(100, '顯示名稱最多 100 字元'),
  description: z.string().max(500, '描述最多 500 字元').optional(),
  tags: z.string().optional(),
  userRegistrationMode: z.enum(['auto', 'whitelist']),
  lineChannelId: z.string().optional(),
  lineChannelSecret: z.string().optional(),
  lineEnabled: z.boolean().optional(),
});

type SettingsForm = z.infer<typeof settingsSchema>;

interface AgentSettingsProps {
  agent: Agent;
  onUpdate?: (agent: Agent) => void;
}

export function AgentSettings({ agent, onUpdate }: AgentSettingsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [lineIntegration, setLineIntegration] = useState<LINEIntegration | null>(null);
  const [whitelistUsers, setWhitelistUsers] = useState<WhitelistUser[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
  } = useForm<SettingsForm>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      displayName: agent.displayName,
      description: agent.description || '',
      tags: Array.isArray(agent.tags) ? agent.tags.join(', ') : (agent.tags || ''),
      userRegistrationMode: agent.userRegistrationMode,
      lineChannelId: '',
      lineChannelSecret: '',
      lineEnabled: false,
    },
  });

  const userRegistrationMode = watch('userRegistrationMode');

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, [agent.agentId]);

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const settings = await getAgentSettings(agent.agentId);
      setLineIntegration(settings.lineIntegration || null);
      setWhitelistUsers(settings.whitelistUsers || []);

      if (settings.lineIntegration) {
        setValue('lineChannelId', settings.lineIntegration.channelId);
        setValue('lineChannelSecret', '••••••••••••');
        setValue('lineEnabled', settings.lineIntegration.isEnabled);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入設定失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: SettingsForm) => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedAgent = await updateAgentSettings(agent.agentId, {
        displayName: data.displayName,
        description: data.description || undefined,
        tags: data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        userRegistrationMode: data.userRegistrationMode,
        lineIntegration: {
          channelId: data.lineChannelId,
          channelSecret: showSecret ? data.lineChannelSecret : undefined,
          isEnabled: data.lineEnabled,
        },
      });

      setSuccess(true);
      onUpdate?.(updatedAgent);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新失敗');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (status: 'running' | 'paused' | 'stopped') => {
    setStatusUpdating(true);
    setError(null);
    try {
      const updatedAgent = await updateAgentStatus(agent.agentId, status);
      onUpdate?.(updatedAgent);
    } catch (err) {
      setError(err instanceof Error ? err.message : '狀態更新失敗');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAddWhitelistUser = async () => {
    if (!newUserId.trim()) return;

    try {
      const user = await addWhitelistUser(agent.agentId, newUserId.trim(), newDisplayName || undefined);
      setWhitelistUsers([...whitelistUsers, user]);
      setNewUserId('');
      setNewDisplayName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '添加用戶失敗');
    }
  };

  const handleRemoveWhitelistUser = async (userId: string) => {
    try {
      await removeWhitelistUser(agent.agentId, userId);
      setWhitelistUsers(whitelistUsers.filter(u => u.userId !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : '移除用戶失敗');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm animate-fade-in">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-status-running/10 text-status-running text-sm animate-fade-in">
          <Check className="h-4 w-4 flex-shrink-0" />
          <span>設定已更新</span>
        </div>
      )}

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
          <CardDescription>更新 Agent 的基本資訊和顯示設定</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">
                顯示名稱 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="displayName"
                placeholder="客戶服務機器人"
                {...register('displayName')}
                className={cn(errors.displayName && 'border-destructive')}
              />
              {errors.displayName && (
                <p className="text-xs text-destructive">{errors.displayName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                placeholder="說明此 Agent 的用途..."
                rows={3}
                {...register('description')}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                最多 500 字元（選填）
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">標籤</Label>
              <Input
                id="tags"
                placeholder="客服, LINE, 新產品（用逗號分隔）"
                {...register('tags')}
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isSaving || !isDirty}>
                {isSaving ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    儲存中...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    儲存變更
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* LINE Integration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>LINE 整合</CardTitle>
              <CardDescription>設定 LINE Messaging Platform 整合</CardDescription>
            </div>
            {lineIntegration?.isEnabled && (
              <Badge variant="status-running">已啟用</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lineChannelId">Channel ID</Label>
            <Input
              id="lineChannelId"
              placeholder="200xxxxxx"
              {...register('lineChannelId')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lineChannelSecret">Channel Secret</Label>
            <div className="relative">
              <Input
                id="lineChannelSecret"
                type={showSecret ? 'text' : 'password'}
                placeholder="••••••••••••"
                {...register('lineChannelSecret')}
                className="pr-20"
              />
              <button
                type="button"
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
              >
                {showSecret ? '隱藏' : '顯示'}
              </button>
            </div>
          </div>

          {lineIntegration && (
            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-muted px-3 py-2 rounded font-mono truncate">
                  {lineIntegration.webhookUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(lineIntegration.webhookUrl)}
                >
                  <Check className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="space-y-1">
              <p className="text-sm font-medium">啟用 LINE 整合</p>
              <p className="text-xs text-muted-foreground">
                啟用後，Agent 將透過 LINE 平台接收訊息
              </p>
            </div>
            <Button
              type="button"
              variant={watch('lineEnabled') ? 'default' : 'outline'}
              size="sm"
              onClick={() => setValue('lineEnabled', !watch('lineEnabled'))}
            >
              {watch('lineEnabled') ? '已啟用' : '啟用'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Registration Mode */}
      <Card>
        <CardHeader>
          <CardTitle>用戶註冊模式</CardTitle>
          <CardDescription>控制誰可以與此 Agent 互動</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userRegistrationMode">註冊模式</Label>
            <Select
              value={userRegistrationMode}
              onValueChange={(value) => setValue('userRegistrationMode', value as 'auto' | 'whitelist')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">自動模式</span>
                    <span className="text-xs text-muted-foreground">
                      任何用戶都可以直接與 Agent 互動
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="whitelist">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">白名單模式</span>
                    <span className="text-xs text-muted-foreground">
                      只有被添加到白名單的用戶可以互動
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {userRegistrationMode === 'whitelist' && (
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <Label>白名單用戶</Label>
                <Badge variant="secondary">{whitelistUsers.length} 位用戶</Badge>
              </div>

              {/* Add user form */}
              <div className="flex gap-2">
                <Input
                  placeholder="LINE User ID"
                  value={newUserId}
                  onChange={(e) => setNewUserId(e.target.value)}
                />
                <Input
                  placeholder="顯示名稱（選填）"
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  className="w-40"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddWhitelistUser}
                  disabled={!newUserId.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Whitelist users list */}
              {whitelistUsers.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {whitelistUsers.map((user) => (
                    <div
                      key={user.userId}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {user.displayName || user.userId}
                        </p>
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          {user.userId}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleRemoveWhitelistUser(user.userId)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  尚未添加白名單用戶
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agent Status Control */}
      <Card>
        <CardHeader>
          <CardTitle>Agent 狀態</CardTitle>
          <CardDescription>控制 Agent 的運行狀態</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={agent.status === 'running' ? 'default' : 'outline'}
              className={cn(
                agent.status === 'running' && 'bg-status-running hover:bg-status-running/90'
              )}
              onClick={() => handleStatusChange('running')}
              disabled={statusUpdating || agent.status === 'running'}
            >
              啟動
            </Button>
            <Button
              type="button"
              variant={agent.status === 'paused' ? 'default' : 'outline'}
              className={cn(
                agent.status === 'paused' && 'bg-status-paused hover:bg-status-paused/90'
              )}
              onClick={() => handleStatusChange('paused')}
              disabled={statusUpdating || agent.status === 'paused'}
            >
              暫停
            </Button>
            <Button
              type="button"
              variant={agent.status === 'stopped' ? 'default' : 'outline'}
              className={cn(
                agent.status === 'stopped' && 'bg-status-stopped hover:bg-status-stopped/90'
              )}
              onClick={() => handleStatusChange('stopped')}
              disabled={statusUpdating || agent.status === 'stopped'}
            >
              停止
            </Button>
          </div>

          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              �前狀態：<span className="font-medium text-foreground">
                {agent.status === 'running' ? '運行中' :
                 agent.status === 'paused' ? '暫停' :
                 agent.status === 'draft' ? '草稿' : '已停止'}
              </span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
